"use client";

import dynamic from "next/dynamic";
import { useEffect, useState } from "react";
import ResponsiveNav from "./Navigation";
const StickyNavigation = dynamic(() => import("./StickyNavigation"), {
  ssr: false, // We use ssr: false because StickyNavigation only shows up upon scrolling down dynamically
});
import { NavigationMenuProvider } from "./NavigationMenuContext";
import { getLatestBlogsList } from "@/lib/ApiClient";
import { BlogListItem } from "@/lib/Types";

// PERF: MegaMenu renders nothing while closed (DesktopPopup returns null), so
// there is no SSR output to preserve. Loading it with ssr:false and mounting it
// only after the first open keeps its chunk (component + navigationData) out of
// the JS that hydrates on every page load. An idle-time prefetch below makes
// the first open feel instant.
const MegaMenu = dynamic(() => import("./MegaMenu"), {
  ssr: false,
});

type NavigationProps = {
  dark?: boolean;
};

export default function Navigation({ dark = true }: NavigationProps) {
  const [open, setOpen] = useState(false);
  const [zIsHigh, setZIsHigh] = useState<boolean>(!!open);
  const resolvedOpen = open ?? false;
  const [latestBlog, setLatestBlog] = useState<BlogListItem[]>([]);
  const [menuEverOpened, setMenuEverOpened] = useState(false);

  const resolvedSetOpen = setOpen ?? (() => { });

  // Mount MegaMenu on first open; keep it mounted afterwards so the close
  // animation still plays.
  useEffect(() => {
    if (resolvedOpen) setMenuEverOpened(true);
  }, [resolvedOpen]);

  // Prefetch the MegaMenu chunk once the browser is idle so the first open
  // doesn't wait on a network request.
  useEffect(() => {
    const prefetch = () => {
      import("./MegaMenu");
    };
    if (typeof window.requestIdleCallback === "function") {
      const id = window.requestIdleCallback(prefetch);
      return () => window.cancelIdleCallback(id);
    }
    const id = window.setTimeout(prefetch, 2500);
    return () => window.clearTimeout(id);
  }, []);

  useEffect(() => {
    if (resolvedOpen) {
      setZIsHigh(true);
      return;
    }
    const timerId = setTimeout(() => setZIsHigh(false), 600);
    return () => clearTimeout(timerId);
  }, [resolvedOpen]);


  useEffect(() => {
    if (!open) return;

    const fetchDetailedBlogs = async () => {
      try {
        const data = await getLatestBlogsList();
        setLatestBlog(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to fetch blogs:", err);
        setLatestBlog([]);
      }
    };

    fetchDetailedBlogs();
  }, [open]);
  return (
    <NavigationMenuProvider>
      <header
        className={`bg-transparent text-white w-full xl:pt-4 relative ${zIsHigh ? "z-[9999]" : "z-[5]"}`}
      >
        <ResponsiveNav
          open={resolvedOpen}
          setOpen={resolvedSetOpen}
          dark={dark}
        />
      </header>
      <StickyNavigation open={resolvedOpen} setOpen={resolvedSetOpen} />
      {menuEverOpened && (
        <MegaMenu open={resolvedOpen} setOpen={resolvedSetOpen} latestBlog={latestBlog} />
      )}
    </NavigationMenuProvider>
  );
}
